// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export type RegistrationMode = "invite_only" | "open_verified_email";
export type AntiAutomationProvider = "altcha" | "turnstile";
export type SmtpTransportSecurity = "starttls_required" | "implicit_tls";
export type SmtpAuthentication = "none" | "password";
export type EmailDeliveryCategory = "alerts" | "invitations" | "account_security" | "general";
export type EmailTemplateKey =
  | "user_invitation"
  | "system_user_invitation"
  | "email_verification"
  | "password_reset"
  | "mfa_code"
  | "monitoring_alert"
  | "general_notification"
  | "internet_interruption_opened"
  | "internet_interruption_resolved"
  | "probe_unavailable_opened"
  | "probe_unavailable_resolved"
  | "notification_contact_opt_in";
export type AuditIdentityRetentionMode = "immediate" | "duration" | "unlimited";
export type NotificationEventFamily = "internet_interruption" | "probe_unavailable";
export type NotificationSystemTemplateKey =
  | "internet_interruption_opened"
  | "internet_interruption_resolved"
  | "probe_unavailable_opened"
  | "probe_unavailable_resolved";

export interface AuditIdentityRetentionSettings {
  readonly mode: AuditIdentityRetentionMode;
  readonly retentionDays: number | null;
  readonly updatedAt: string;
}

export interface UpdateAuditIdentityRetentionInput {
  readonly mode: AuditIdentityRetentionMode;
  readonly retentionDays: number | null;
}

export interface AuditIdentityPurgeResult {
  readonly purgedIdentities: number;
}

export interface RegistrationSettings {
  readonly mode: RegistrationMode;
  readonly antiAutomationProvider: AntiAutomationProvider | null;
  readonly blockDisposableEmailDomains: boolean;
  readonly blockUntrustedEmailDomains: boolean;
  readonly openSignupOperational: boolean;
}

export interface ProbeOperationsSettings {
  readonly enrollmentCodeTtlSeconds: number;
  readonly enrollmentClaimRateLimitMax: number;
  readonly enrollmentClaimRateLimitWindowSeconds: number;
  readonly uploadDiagnosticsRetentionDays: number;
  readonly uploadWarningWifiRssiDbm: number;
  readonly uploadWarningConsecutiveFailures: number;
  readonly uploadWarningQueueUsageRatio: number;
  readonly updatedAt: string;
}

export interface NotificationRuntimeSettings {
  readonly alertEvaluationIntervalMs: number;
  readonly alertEvaluationBatchSize: number;
  readonly outboxPollIntervalMs: number;
  readonly outboxBatchSize: number;
  readonly outboxLeaseMs: number;
  readonly outboxRetryBaseDelayMs: number;
  readonly outboxRetryMaxDelayMs: number;
  readonly outboxMaxAttempts: number;
  readonly updatedAt: string;
}

export interface UpdateNotificationRuntimeSettingsInput {
  readonly alertEvaluationIntervalMs: number;
  readonly alertEvaluationBatchSize: number;
  readonly outboxPollIntervalMs: number;
  readonly outboxBatchSize: number;
  readonly outboxLeaseMs: number;
  readonly outboxRetryBaseDelayMs: number;
  readonly outboxRetryMaxDelayMs: number;
  readonly outboxMaxAttempts: number;
}

export interface UpdateProbeOperationsSettingsInput {
  readonly enrollmentCodeTtlSeconds: number;
  readonly enrollmentClaimRateLimitMax: number;
  readonly enrollmentClaimRateLimitWindowSeconds: number;
  readonly uploadDiagnosticsRetentionDays: number;
  readonly uploadWarningWifiRssiDbm: number;
  readonly uploadWarningConsecutiveFailures: number;
  readonly uploadWarningQueueUsageRatio: number;
}

export interface NotificationEventDefault {
  readonly eventFamily: NotificationEventFamily;
  readonly thresholdMs: number;
  readonly sendOpened: boolean;
  readonly sendRecovery: boolean;
  readonly openedTemplateKey: NotificationSystemTemplateKey;
  readonly resolvedTemplateKey: NotificationSystemTemplateKey;
  readonly updatedAt: string;
}

export interface NotificationEventDefaultList {
  readonly defaults: readonly NotificationEventDefault[];
}

export interface UpdateNotificationEventDefaultInput {
  readonly thresholdMs: number;
  readonly sendOpened: boolean;
  readonly sendRecovery: boolean;
  readonly openedTemplateKey: NotificationSystemTemplateKey;
  readonly resolvedTemplateKey: NotificationSystemTemplateKey;
}

export interface UpdateRegistrationSettingsInput {
  readonly mode: RegistrationMode;
  readonly antiAutomationProvider: AntiAutomationProvider | null;
  readonly blockDisposableEmailDomains: boolean;
  readonly blockUntrustedEmailDomains: boolean;
}

export interface SmtpProfile {
  readonly id: string;
  readonly name: string;
  readonly enabled: boolean;
  readonly host: string | null;
  readonly port: number | null;
  readonly transportSecurity: SmtpTransportSecurity;
  readonly authentication: SmtpAuthentication;
  readonly username: string | null;
  readonly passwordConfigured: boolean;
  readonly fromEmail: string | null;
  readonly fromName: string | null;
  readonly replyToEmail: string | null;
  readonly configurationComplete: boolean;
  readonly deliveryOperational: boolean;
}

export interface SmtpProfileList {
  readonly profiles: readonly SmtpProfile[];
  readonly credentialEncryptionAvailable: boolean;
}

export interface SaveSmtpProfileInput {
  readonly name: string;
  readonly enabled: boolean;
  readonly host: string | null;
  readonly port: number | null;
  readonly transportSecurity: SmtpTransportSecurity;
  readonly authentication: SmtpAuthentication;
  readonly username: string | null;
  readonly password?: string;
  readonly clearPassword?: boolean;
  readonly fromEmail: string | null;
  readonly fromName: string | null;
  readonly replyToEmail: string | null;
}

export interface EmailDeliveryRoute {
  readonly category: EmailDeliveryCategory;
  readonly smtpProfileId: string | null;
  readonly smtpProfileName: string | null;
  readonly operational: boolean;
}

export interface EmailDeliveryRouting {
  readonly routes: readonly EmailDeliveryRoute[];
}

export interface EmailTemplatePlaceholder {
  readonly key: string;
  readonly description: string;
  readonly example: string;
  readonly required: boolean;
}

export interface EmailTemplate {
  readonly key: EmailTemplateKey;
  readonly displayName: string;
  readonly description: string;
  readonly category: EmailDeliveryCategory;
  readonly enabled: boolean;
  readonly subjectTemplate: string;
  readonly textBodyTemplate: string;
  readonly htmlBodyTemplate: string | null;
  readonly placeholders: readonly EmailTemplatePlaceholder[];
  readonly revision: number;
  readonly updatedAt: string;
}

export interface EmailTemplateList {
  readonly templates: readonly EmailTemplate[];
}

export interface SaveEmailTemplateInput {
  readonly enabled: boolean;
  readonly subjectTemplate: string;
  readonly textBodyTemplate: string;
  readonly htmlBodyTemplate: string | null;
}

export interface EmailTemplatePreview {
  readonly subject: string;
  readonly textBody: string;
  readonly htmlBody: string | null;
  readonly exampleVariables: Readonly<Record<string, string>>;
}

export interface SendTestEmailInput {
  readonly recipientEmail: string;
  readonly templateKey: EmailTemplateKey;
}

export interface SendTestEmailResult {
  readonly status: "accepted";
  readonly messageId: string | null;
  readonly acceptedAt: string;
  readonly auditRecorded: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readBoolean(value: unknown, fieldName: string): boolean {
  if (typeof value !== "boolean") {
    throw new Error(`System settings response field ${fieldName} is invalid`);
  }
  return value;
}

function readNullableString(value: unknown, fieldName: string): string | null {
  if (value !== null && typeof value !== "string") {
    throw new Error(`System settings response field ${fieldName} is invalid`);
  }
  return value as string | null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`System settings response field ${fieldName} is invalid`);
  }
  return value;
}

function readRegistrationSettings(payload: unknown): RegistrationSettings {
  if (!isRecord(payload)) {
    throw new Error("Registration settings response is invalid");
  }
  if (payload.mode !== "invite_only" && payload.mode !== "open_verified_email") {
    throw new Error("Registration settings response field mode is invalid");
  }
  if (
    payload.antiAutomationProvider !== null &&
    payload.antiAutomationProvider !== "altcha" &&
    payload.antiAutomationProvider !== "turnstile"
  ) {
    throw new Error("Registration settings response field antiAutomationProvider is invalid");
  }
  return {
    mode: payload.mode,
    antiAutomationProvider: payload.antiAutomationProvider,
    blockDisposableEmailDomains: readBoolean(
      payload.blockDisposableEmailDomains,
      "blockDisposableEmailDomains",
    ),
    blockUntrustedEmailDomains: readBoolean(
      payload.blockUntrustedEmailDomains,
      "blockUntrustedEmailDomains",
    ),
    openSignupOperational: readBoolean(payload.openSignupOperational, "openSignupOperational"),
  };
}

function readAuditIdentityRetention(payload: unknown): AuditIdentityRetentionSettings {
  if (!isRecord(payload) ||
      (payload.mode !== "immediate" && payload.mode !== "duration" && payload.mode !== "unlimited") ||
      (payload.retentionDays !== null && typeof payload.retentionDays !== "number")) {
    throw new Error("Audit identity retention response is invalid");
  }
  return {
    mode: payload.mode,
    retentionDays: payload.retentionDays,
    updatedAt: readString(payload.updatedAt, "updatedAt"),
  };
}

function readNumber(value: unknown, fieldName: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`System settings response field ${fieldName} is invalid`);
  }
  return value;
}

function readProbeOperationsSettings(payload: unknown): ProbeOperationsSettings {
  if (!isRecord(payload)) {
    throw new Error("Probe operations settings response is invalid");
  }
  return {
    enrollmentCodeTtlSeconds: readNumber(
      payload.enrollmentCodeTtlSeconds,
      "enrollmentCodeTtlSeconds",
    ),
    enrollmentClaimRateLimitMax: readNumber(
      payload.enrollmentClaimRateLimitMax,
      "enrollmentClaimRateLimitMax",
    ),
    enrollmentClaimRateLimitWindowSeconds: readNumber(
      payload.enrollmentClaimRateLimitWindowSeconds,
      "enrollmentClaimRateLimitWindowSeconds",
    ),
    uploadDiagnosticsRetentionDays: readNumber(
      payload.uploadDiagnosticsRetentionDays,
      "uploadDiagnosticsRetentionDays",
    ),
    uploadWarningWifiRssiDbm: readNumber(
      payload.uploadWarningWifiRssiDbm,
      "uploadWarningWifiRssiDbm",
    ),
    uploadWarningConsecutiveFailures: readNumber(
      payload.uploadWarningConsecutiveFailures,
      "uploadWarningConsecutiveFailures",
    ),
    uploadWarningQueueUsageRatio: readNumber(
      payload.uploadWarningQueueUsageRatio,
      "uploadWarningQueueUsageRatio",
    ),
    updatedAt: readString(payload.updatedAt, "updatedAt"),
  };
}

function readNotificationRuntimeSettings(payload: unknown): NotificationRuntimeSettings {
  if (!isRecord(payload)) {
    throw new Error("Notification runtime settings response is invalid");
  }
  return {
    alertEvaluationIntervalMs: readNumber(
      payload.alertEvaluationIntervalMs,
      "alertEvaluationIntervalMs",
    ),
    alertEvaluationBatchSize: readNumber(
      payload.alertEvaluationBatchSize,
      "alertEvaluationBatchSize",
    ),
    outboxPollIntervalMs: readNumber(payload.outboxPollIntervalMs, "outboxPollIntervalMs"),
    outboxBatchSize: readNumber(payload.outboxBatchSize, "outboxBatchSize"),
    outboxLeaseMs: readNumber(payload.outboxLeaseMs, "outboxLeaseMs"),
    outboxRetryBaseDelayMs: readNumber(
      payload.outboxRetryBaseDelayMs,
      "outboxRetryBaseDelayMs",
    ),
    outboxRetryMaxDelayMs: readNumber(
      payload.outboxRetryMaxDelayMs,
      "outboxRetryMaxDelayMs",
    ),
    outboxMaxAttempts: readNumber(payload.outboxMaxAttempts, "outboxMaxAttempts"),
    updatedAt: readString(payload.updatedAt, "updatedAt"),
  };
}

function readAuditIdentityPurgeResult(payload: unknown): AuditIdentityPurgeResult {
  if (!isRecord(payload) || typeof payload.purgedIdentities !== "number") {
    throw new Error("Audit identity purge response is invalid");
  }
  return { purgedIdentities: payload.purgedIdentities };
}

function readSmtpProfile(payload: unknown): SmtpProfile {
  if (!isRecord(payload)) {
    throw new Error("SMTP profile response is invalid");
  }
  if (
    payload.transportSecurity !== "starttls_required" &&
    payload.transportSecurity !== "implicit_tls"
  ) {
    throw new Error("SMTP settings response field transportSecurity is invalid");
  }
  if (payload.authentication !== "none" && payload.authentication !== "password") {
    throw new Error("SMTP settings response field authentication is invalid");
  }
  if (payload.port !== null && typeof payload.port !== "number") {
    throw new Error("SMTP settings response field port is invalid");
  }
  return {
    id: readString(payload.id, "id"),
    name: readString(payload.name, "name"),
    enabled: readBoolean(payload.enabled, "enabled"),
    host: readNullableString(payload.host, "host"),
    port: payload.port as number | null,
    transportSecurity: payload.transportSecurity,
    authentication: payload.authentication,
    username: readNullableString(payload.username, "username"),
    passwordConfigured: readBoolean(payload.passwordConfigured, "passwordConfigured"),
    fromEmail: readNullableString(payload.fromEmail, "fromEmail"),
    fromName: readNullableString(payload.fromName, "fromName"),
    replyToEmail: readNullableString(payload.replyToEmail, "replyToEmail"),
    configurationComplete: readBoolean(payload.configurationComplete, "configurationComplete"),
    deliveryOperational: readBoolean(payload.deliveryOperational, "deliveryOperational"),
  };
}

function readSmtpProfileList(payload: unknown): SmtpProfileList {
  if (!isRecord(payload) || !Array.isArray(payload.profiles)) {
    throw new Error("SMTP profile list response is invalid");
  }
  return {
    profiles: payload.profiles.map((profile: unknown): SmtpProfile => readSmtpProfile(profile)),
    credentialEncryptionAvailable: readBoolean(
      payload.credentialEncryptionAvailable,
      "credentialEncryptionAvailable",
    ),
  };
}

function readEmailDeliveryCategory(value: unknown): EmailDeliveryCategory {
  if (value === "alerts" || value === "invitations" || value === "account_security" || value === "general") {
    return value;
  }
  throw new Error("Email delivery category is invalid");
}

function readEmailDeliveryRoute(payload: unknown): EmailDeliveryRoute {
  if (!isRecord(payload)) {
    throw new Error("Email delivery route response is invalid");
  }
  return {
    category: readEmailDeliveryCategory(payload.category),
    smtpProfileId: readNullableString(payload.smtpProfileId, "smtpProfileId"),
    smtpProfileName: readNullableString(payload.smtpProfileName, "smtpProfileName"),
    operational: readBoolean(payload.operational, "operational"),
  };
}

function readEmailDeliveryRouting(payload: unknown): EmailDeliveryRouting {
  if (!isRecord(payload) || !Array.isArray(payload.routes)) {
    throw new Error("Email delivery routing response is invalid");
  }
  return {
    routes: payload.routes.map(
      (route: unknown): EmailDeliveryRoute => readEmailDeliveryRoute(route),
    ),
  };
}

function readEmailTemplateKey(value: unknown): EmailTemplateKey {
  if (value === "user_invitation" || value === "system_user_invitation" || value === "email_verification" ||
      value === "password_reset" || value === "mfa_code" ||
      value === "monitoring_alert" || value === "general_notification" ||
      value === "internet_interruption_opened" || value === "internet_interruption_resolved" ||
      value === "probe_unavailable_opened" || value === "probe_unavailable_resolved" ||
      value === "notification_contact_opt_in") {
    return value;
  }
  throw new Error("Email template key is invalid");
}

function readNotificationEventFamily(value: unknown, fieldName: string): NotificationEventFamily {
  if (value === "internet_interruption" || value === "probe_unavailable") {
    return value;
  }
  throw new Error(`System settings response field ${fieldName} is invalid`);
}

function readNotificationSystemTemplateKey(
  value: unknown,
  fieldName: string,
): NotificationSystemTemplateKey {
  if (
    value === "internet_interruption_opened" ||
    value === "internet_interruption_resolved" ||
    value === "probe_unavailable_opened" ||
    value === "probe_unavailable_resolved"
  ) {
    return value;
  }
  throw new Error(`System settings response field ${fieldName} is invalid`);
}

function readNotificationEventDefault(
  payload: unknown,
  fieldName: string,
): NotificationEventDefault {
  if (!isRecord(payload)) {
    throw new Error(`Notification default response field ${fieldName} is invalid`);
  }
  return {
    eventFamily: readNotificationEventFamily(payload.eventFamily, `${fieldName}.eventFamily`),
    thresholdMs: readNumber(payload.thresholdMs, `${fieldName}.thresholdMs`),
    sendOpened: readBoolean(payload.sendOpened, `${fieldName}.sendOpened`),
    sendRecovery: readBoolean(payload.sendRecovery, `${fieldName}.sendRecovery`),
    openedTemplateKey: readNotificationSystemTemplateKey(
      payload.openedTemplateKey,
      `${fieldName}.openedTemplateKey`,
    ),
    resolvedTemplateKey: readNotificationSystemTemplateKey(
      payload.resolvedTemplateKey,
      `${fieldName}.resolvedTemplateKey`,
    ),
    updatedAt: readString(payload.updatedAt, `${fieldName}.updatedAt`),
  };
}

function readNotificationEventDefaultList(payload: unknown): NotificationEventDefaultList {
  if (!isRecord(payload) || !Array.isArray(payload.defaults)) {
    throw new Error("Notification defaults response is invalid");
  }
  return {
    defaults: payload.defaults.map(
      (entry: unknown, index: number): NotificationEventDefault =>
        readNotificationEventDefault(entry, `defaults.${index}`),
    ),
  };
}

function readEmailTemplatePlaceholder(payload: unknown): EmailTemplatePlaceholder {
  if (!isRecord(payload)) {
    throw new Error("Email template placeholder is invalid");
  }
  return {
    key: readString(payload.key, "placeholder.key"),
    description: readString(payload.description, "placeholder.description"),
    example: readString(payload.example, "placeholder.example"),
    required: readBoolean(payload.required, "placeholder.required"),
  };
}

function readEmailTemplate(payload: unknown): EmailTemplate {
  if (!isRecord(payload) || !Array.isArray(payload.placeholders) ||
      typeof payload.revision !== "number") {
    throw new Error("Email template response is invalid");
  }
  return {
    key: readEmailTemplateKey(payload.key),
    displayName: readString(payload.displayName, "displayName"),
    description: readString(payload.description, "description"),
    category: readEmailDeliveryCategory(payload.category),
    enabled: readBoolean(payload.enabled, "enabled"),
    subjectTemplate: readString(payload.subjectTemplate, "subjectTemplate"),
    textBodyTemplate: readString(payload.textBodyTemplate, "textBodyTemplate"),
    htmlBodyTemplate: readNullableString(payload.htmlBodyTemplate, "htmlBodyTemplate"),
    placeholders: payload.placeholders.map(
      (placeholder: unknown): EmailTemplatePlaceholder => readEmailTemplatePlaceholder(placeholder),
    ),
    revision: payload.revision,
    updatedAt: readString(payload.updatedAt, "updatedAt"),
  };
}

function readEmailTemplateList(payload: unknown): EmailTemplateList {
  if (!isRecord(payload) || !Array.isArray(payload.templates)) {
    throw new Error("Email template list response is invalid");
  }
  return {
    templates: payload.templates.map(
      (template: unknown): EmailTemplate => readEmailTemplate(template),
    ),
  };
}

function readStringRecord(value: unknown, fieldName: string): Readonly<Record<string, string>> {
  if (!isRecord(value)) {
    throw new Error(`Email template preview field ${fieldName} is invalid`);
  }
  const result: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    result[key] = readString(entry, `${fieldName}.${key}`);
  }
  return result;
}

function readEmailTemplatePreview(payload: unknown): EmailTemplatePreview {
  if (!isRecord(payload)) {
    throw new Error("Email template preview response is invalid");
  }
  return {
    subject: readString(payload.subject, "subject"),
    textBody: readString(payload.textBody, "textBody"),
    htmlBody: readNullableString(payload.htmlBody, "htmlBody"),
    exampleVariables: readStringRecord(payload.exampleVariables, "exampleVariables"),
  };
}

function readSendTestEmailResult(payload: unknown): SendTestEmailResult {
  if (!isRecord(payload) || payload.status !== "accepted") {
    throw new Error("SMTP test response is invalid");
  }
  return {
    status: payload.status,
    messageId: readNullableString(payload.messageId, "messageId"),
    acceptedAt: readString(payload.acceptedAt, "acceptedAt"),
    auditRecorded: readBoolean(payload.auditRecorded, "auditRecorded"),
  };
}

export async function loadRegistrationSettings(): Promise<RegistrationSettings> {
  return readRegistrationSettings(await requestJson("/api/v1/system/settings/registration"));
}

export async function loadProbeOperationsSettings(): Promise<ProbeOperationsSettings> {
  return readProbeOperationsSettings(
    await requestJson("/api/v1/system/settings/probe-operations"),
  );
}

export async function loadNotificationEventDefaults(): Promise<NotificationEventDefaultList> {
  return readNotificationEventDefaultList(
    await requestJson("/api/v1/system/settings/notification-defaults"),
  );
}

export async function loadNotificationRuntimeSettings(): Promise<NotificationRuntimeSettings> {
  return readNotificationRuntimeSettings(
    await requestJson("/api/v1/system/settings/notification-runtime"),
  );
}

export async function saveNotificationRuntimeSettings(
  input: UpdateNotificationRuntimeSettingsInput,
): Promise<NotificationRuntimeSettings> {
  return readNotificationRuntimeSettings(
    await requestJson("/api/v1/system/settings/notification-runtime", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
}

export async function saveNotificationEventDefault(
  eventFamily: NotificationEventFamily,
  input: UpdateNotificationEventDefaultInput,
): Promise<NotificationEventDefault> {
  return readNotificationEventDefault(
    await requestJson(`/api/v1/system/settings/notification-defaults/${eventFamily}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
    "default",
  );
}

export async function saveProbeOperationsSettings(
  input: UpdateProbeOperationsSettingsInput,
): Promise<ProbeOperationsSettings> {
  return readProbeOperationsSettings(
    await requestJson("/api/v1/system/settings/probe-operations", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
}

export async function loadAuditIdentityRetention(): Promise<AuditIdentityRetentionSettings> {
  return readAuditIdentityRetention(
    await requestJson("/api/v1/system/settings/audit-identity-retention"),
  );
}

export async function saveAuditIdentityRetention(
  input: UpdateAuditIdentityRetentionInput,
): Promise<AuditIdentityRetentionSettings> {
  return readAuditIdentityRetention(
    await requestJson("/api/v1/system/settings/audit-identity-retention", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
}

export async function purgeExpiredAuditIdentities(): Promise<AuditIdentityPurgeResult> {
  return readAuditIdentityPurgeResult(
    await requestJson(
      "/api/v1/system/settings/audit-identity-retention/purge-expired",
      { method: "POST" },
    ),
  );
}

export async function saveRegistrationSettings(
  input: UpdateRegistrationSettingsInput,
): Promise<RegistrationSettings> {
  return readRegistrationSettings(
    await requestJson("/api/v1/system/settings/registration", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
}

export async function loadSmtpProfiles(): Promise<SmtpProfileList> {
  return readSmtpProfileList(await requestJson("/api/v1/system/settings/smtp-profiles"));
}

export async function createSmtpProfile(input: SaveSmtpProfileInput): Promise<SmtpProfile> {
  return readSmtpProfile(
    await requestJson("/api/v1/system/settings/smtp-profiles", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
}

export async function updateSmtpProfile(
  profileId: string,
  input: SaveSmtpProfileInput,
): Promise<SmtpProfile> {
  return readSmtpProfile(
    await requestJson(`/api/v1/system/settings/smtp-profiles/${profileId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
}

export async function deleteSmtpProfile(profileId: string): Promise<void> {
  await requestJson(`/api/v1/system/settings/smtp-profiles/${profileId}`, { method: "DELETE" });
}

export async function loadEmailDeliveryRouting(): Promise<EmailDeliveryRouting> {
  return readEmailDeliveryRouting(await requestJson("/api/v1/system/settings/email-routing"));
}

export async function saveEmailDeliveryRoute(
  category: EmailDeliveryCategory,
  smtpProfileId: string | null,
): Promise<EmailDeliveryRoute> {
  return readEmailDeliveryRoute(
    await requestJson(`/api/v1/system/settings/email-routing/${category}`, {
      method: "PUT",
      body: JSON.stringify({ smtpProfileId }),
    }),
  );
}

export async function loadEmailTemplates(): Promise<EmailTemplateList> {
  return readEmailTemplateList(
    await requestJson("/api/v1/system/settings/email-templates"),
  );
}

export async function saveEmailTemplate(
  templateKey: EmailTemplateKey,
  input: SaveEmailTemplateInput,
): Promise<EmailTemplate> {
  return readEmailTemplate(
    await requestJson(`/api/v1/system/settings/email-templates/${templateKey}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  );
}

export async function previewEmailTemplate(
  templateKey: EmailTemplateKey,
  input: SaveEmailTemplateInput,
): Promise<EmailTemplatePreview> {
  return readEmailTemplatePreview(
    await requestJson(`/api/v1/system/settings/email-templates/${templateKey}/preview`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
}

export async function sendSmtpTestEmail(
  profileId: string,
  input: SendTestEmailInput,
): Promise<SendTestEmailResult> {
  return readSendTestEmailResult(
    await requestJson(`/api/v1/system/settings/smtp-profiles/${profileId}/test-email`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
}
